import { getLazyFactoryID } from '@wix/editor-elements-conventions';
import { createException } from '../errors/RegistryError';
import { isBrowserMainThread } from '../flags';
import { getEnvironment, requireAMDModule } from './evaluateBundle';

const SCRIPT_LOADING_TIME_THRESHOLD = 10000;

export function getScriptTag(url: string) {
  if (!isBrowserMainThread) {
    return null;
  }

  const element = document.querySelector(`script[src="${url}"]`);
  return element;
}

export function getPendingModel<TResponse = any>({
  id,
  url,
}: {
  id?: string;
  url: string;
}) {
  const element = getScriptTag(url);

  if (element) {
    return new Promise<TResponse | void>((resolve, reject) => {
      let isOnLoadCalled = false;
      element.addEventListener('load', async () => {
        isOnLoadCalled = true;

        if (id) {
          resolve(await getGlobalModel<TResponse>(id));
        } else {
          resolve();
        }
      });

      /**
       * In general, this case should never happen.
       * If `addEventListener` was added on the already loaded script tag –
       * callback never will be called, which means that `getPendingModel()` function is never resolved.
       */
      setTimeout(() => {
        if (!isOnLoadCalled) {
          const error = createException('`getModel` is not resolved')
            .withUrl(url)
            .withHint(
              'probably, event listener was added on already loaded script',
            );

          reject(error.message);
        }
      }, SCRIPT_LOADING_TIME_THRESHOLD);
    });
  }

  return null;
}

export async function getGlobalFactoryModel<TResponse = any>(id: string) {
  const factory = await getGlobalModel(getLazyFactoryID(id));

  if (factory && !factory.invoked) {
    factory.invoked = true;

    factory();
  }

  /**
   * Probably we need to check pending script tags
   */
  return getGlobalModel<TResponse>(id);
}

export function getGlobalModel<TResponse = any>(id: string) {
  const env = getEnvironment({ id });

  const model = env.self[id] as TResponse;

  if (!model && typeof env.define === 'function') {
    return requireAMDModule<TResponse>(id);
  }

  return model;
}

export async function getModel(id: string, url: string) {
  const model = await getGlobalModel(id);

  if (model) {
    return model;
  }

  return getPendingModel({ id, url });
}
